import React from 'react';
import PropTypes from 'prop-types';
import TableSorter from '../table-sorter/TableSorter';
import AjaxSpinner from '../ajax-spinner/AjaxSpinner';
import CommonAppSetup from '../../common/common-app-setup/CommonAppSetup';
import Row from './Row';
import DragDropRow from './DragDropRow';
import ColumnInformation from './ColumnInformation';
import HeaderRow from './HeaderRow';
import Filter from './Filter';
import handleEvent from '../../common/events/HandleEvent';
import clone from 'clone';
import _ from 'lodash';
import FilterType, {filterIsSelect} from './FilterType';
import momentCache from "../../common/moment/MomentCache";
import Components from '../../common/Components';
import joinClassNames from "../../common/JoinClassNames";
import {DragDropContext} from "react-dnd";
import HTML5Backend from 'react-dnd-html5-backend'
import dragDropEngine from "./DragDropEngine";

const constants = {
	MORE_RECORDS_MESSAGE: 'There are more results. Click here to view all records.',
};

const propTypes = {
	// should the ajax spinner been shown for the table?
	ajaxSpinner: PropTypes.bool,

	// className css classes to add to the table element
	className: PropTypes.string,

	// array of ColumnInformation telling column information to show
	columns: PropTypes.arrayOf(PropTypes.instanceOf(ColumnInformation)).isRequired,

	// which column is sorted by default (uses the field name of the column to match)
	defaultSortField: PropTypes.string,

	// add filter input for each column
	filters: PropTypes.bool,

	// if the filters need to be set
	// { filterName: value, filterName: value, ... }
	filterValues: PropTypes.object,

	// If this is not undefined then show the bottom button; generally this is a "more records" announcement
	greenBottomButtonText: PropTypes.string,

	// list of records to show
	list: PropTypes.array.isRequired,

	// a search was performed and got no results, so show message that nothing was found
	noRecordsFound: PropTypes.bool,

	// called when a row is dropped at end of a drag (startDragRowIndex, endDragRowIndex) : caller can then manipulate the data however they want
	onDragDrop: PropTypes.func,

	// the caller can respond to the green bottom button press
	onGreenBottomButtonClick: PropTypes.func,

	// when a row is clicked, this function is called with the object clicked sent back
	// record => whatever you want to do with the record
	onRowClick: PropTypes.func,

	// when a row is double clicked, this function is called with the object clicked sent back
	onRowDoubleClick: PropTypes.func,

	// when the row is updated, this function is called, custom cells like a delete button need custom setup when updated
	onRowUpdate: PropTypes.func,

	// the parent of this component can be notified when the sort changes
	// useful for knowing current sort order so that if the data list gets refilled (timed ajax request?) it can sort the data how the table expects it
	onTableSorterSet: PropTypes.func,

	// which records are selected (their tr will get a "selected" class), pass in actual records that are selected so that selectedList[x] === list[y]
	selectedList: PropTypes.array,

	// show only the x first results
	showLimit: PropTypes.number,

	// If you'd like to add custom classes to the button
	showLimitClassName: PropTypes.string,

	// totals (object) to display in tfoot
	// keys HAVE TO be similar as the records in this.props.list
	totals: PropTypes.object,

	// css classes for the row
	trClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
};

const defaultProps = {
	ajaxSpinner: false,
	className: undefined,
	defaultSortField: undefined,
	filters: false,
	filterValues: undefined,
	greenBottomButtonText: undefined,
	noRecordsFound: false,
	onDragDrop: undefined,
	onGreenBottomButtonClick: undefined,
	onRowClick: undefined,
	onRowDoubleClick: undefined,
	onRowUpdate: undefined,
	onTableSorterSet: undefined,
	selectedList: undefined,
	showLimit: 0,
	showLimitClassName: '',
	totals: undefined,
	trClassName: undefined,
};

class Table extends React.Component {
	constructor(props) {
		super(props);

		this.headerOnRowClick = this.headerOnRowClick.bind(this);
		this.filterChange = this.filterChange.bind(this);

		// Sort by the defaultSortField column. Otherwise do not indicate the sorted column.
		let defaultSort = false;
		if (this.props.defaultSortField) {
			defaultSort = this.props.columns.find(c => c.field === this.props.defaultSortField).tableSorterInformation;
			if (!defaultSort) {
				console.error('Table default sort defined but could not find default table sorter information; Probably a mismatching default sort and tableSorterInformation field name:', this.props.defaultSortField, this.props.columns);
			}
		} else {
			// use first available sortable column
			defaultSort = this.props.columns.map(column => column.tableSorterInformation).filter(tableSorterInformation => tableSorterInformation).shift();
		}

		this.columnMap = _.keyBy(this.props.columns, c => c.field);

		this.defaultfilterValuesShown = {};
		this.props.columns.map(column => column.filterDefaultValue ? this.defaultfilterValuesShown[column.field] = column.filterDefaultValue : undefined);

		// set a table sorter
		this.state = {
			tableSorter: defaultSort && new TableSorter(defaultSort),
			showLimit: this.props.showLimit,
			// key is the field, value is the current filter value for that field
			filterValuesShown: this.defaultfilterValuesShown,
			// the filtered list of data
			filterList: !_.isEmpty(this.defaultfilterValuesShown) ? this.filterList(this.defaultfilterValuesShown) : undefined,
		};
		// when filter changes, delay before filtering
		this.filterTimer = undefined;

		this.tableSorterChanged();

		// if no default sort, default to
		this.props.list && this.state.tableSorter && this.state.tableSorter.sort && this.state.tableSorter.sort(this.props.list);

		if (this.props.onTableSorterSet) {
			console.warn('The onTableSorterSet property of the Table component is deprecated. Tables now resort when the list changes so that the parent does not need to track the sorter. onTableSorterSet will be removed in a future version.');
		}

		if (this.onDragDrop && this.state.tableSorter) {
			console.warn('Tables can not sort AND drag and drop. What good is drag and dropping a custom order if a column can just resort the data.');
		}
	}

	componentDidMount() {
		const findRecordIndex = record => this.props.list.findIndex(item => item === record);
		dragDropEngine.setProviders(
			// record information about the start drag
			props => ({ startIndex: findRecordIndex(props.record), startRecord: props.record }),

			// notify parent that a drag has occurred
			(props, monitor) => {
				this.props.onDragDrop && this.props.onDragDrop(monitor.getItem().startIndex, findRecordIndex(props.record));
			},
		);
	}

	/**
	 * If a new list is used, we filter it and update the state
	 */
	componentDidUpdate(prevProps) {
		this.state.tableSorter && this.state.tableSorter.sort(this.props.list);
		if (prevProps.list !== this.props.list || prevProps.filterValues !== this.props.filterValues) {
			const filterValues = {...this.state.filterValuesShown, ...this.props.filterValues};
			this.props.filters && this.setState({ filterList: this.filterList(filterValues), filterValuesShown: filterValues });
		}
	}

	/**
	 * tell the parent component that the sorter is changed, see proptypes.onTableSorterSet for more information
	 */
	tableSorterChanged() {
		if (this.props.onTableSorterSet) {
			this.props.onTableSorterSet(this.state.tableSorter);
		}
	}

	headerOnRowClick(tableSorterInformation) {
		if (tableSorterInformation) {
			this.state.tableSorter.toggleSort(tableSorterInformation);
			this.state.tableSorter.sort(this.props.list);
			this.state.filterList && this.state.tableSorter.sort(this.state.filterList);

			this.tableSorterChanged();

			// tell react to redraw sorted list
			this.setState({tableSorter: this.state.tableSorter});
		}
	}

	/**
	 * delay before actually filtering list data in case they are typing quickly
	 *
	 * @param columnInformation the column being filtered
	 * @param value the value of the filter to test the data against
	 * @param noDelay for SELECT filters, just filter immediately
	 */
	filterChange(columnInformation, value, noDelay) {
		if (this.filterTimer) {
			clearTimeout(this.filterTimer);
			this.filterTimer = undefined;
		}

		// show the new value
		const newFilterValuesShown = Object.assign({}, this.state.filterValuesShown, {[columnInformation.field]: value});

		// if no delay then set state to new filtered state
		if (noDelay) {
			const newState = clone(this.state);
			newState.filterValuesShown = newFilterValuesShown;
			newState.filterList = this.filterList(newState.filterValuesShown);
			this.setState(newState, () => this.filterChangeCallback(columnInformation, value));
		} else {
			// if delaying, then set state to show new updated filter values
			this.setState({filterValuesShown: newFilterValuesShown});

			// delay doing filtering (this may be interrupted if another filter key is pressed)
			this.filterTimer = setTimeout(() => this.setState({filterList: this.filterList(this.state.filterValuesShown)}, () => this.filterChangeCallback(columnInformation, value)), 500);
		}
	}

	filterChangeCallback(columnInformation, value) {
		columnInformation.filterCallback && columnInformation.filterCallback(this.state.filterList, value);
	}

	/**
	 * default value for filter value based on filter type
	 *
	 * @param filterType FilterType enum type of filter
	 * @return {*} default value for the filterType
	 */
	defaultFilterValue(filterType) {
		return filterType === FilterType.DATE_RANGE ? {from: '', to: ''} : '';
	}

	/**
	 * filter the data list and return the reduced list of matching records
	 * @param filters key:value pairs of filters, use columnInformations to determine data types
	 */
	filterList(filters) {
		const momentDates = {};

		return this.props.list.filter(data => _.every(filters, (value, key) => {
			let momentFrom;
			let momentTo;
			if (this.columnMap[key].filterType === FilterType.DATE_RANGE) {
				if (momentDates[key]) {
					momentFrom = momentDates[key].from;
					momentTo = momentDates[key].to;
				} else {
					momentFrom = value && momentCache.moment(value.from);
					momentTo = value && momentCache.moment(value.to);
					momentDates[key] = {from: momentFrom, to: momentTo};
				}
			}

			let result = true;
			if (value || value === false) {
				// if filtering by a menu, then do exact match
				let dataTest;
				if (this.columnMap[key].filterType === FilterType.DATE_RANGE) {
					dataTest = momentCache.moment(data[key]);
				} else {
					dataTest = ('' + data[key]).toUpperCase();
				}

				if (filterIsSelect(this.columnMap[key].filterType)) {
					// a multi select can have an array of values, and if so then check that any conditions match; default to show if there are no filters
					result = _.castArray(value).reduce((matches, test) => matches || (dataTest === ('' + test).toUpperCase()), !_.castArray(value).length);
				} else if (this.columnMap[key].filterType === FilterType.DATE_RANGE) {
					result = (momentFrom.isValid || momentTo.isValid() || dataTest.isValid()) && (!momentFrom.isValid() || momentFrom.isSameOrBefore(dataTest)) && (!momentTo.isValid() || momentTo.isSameOrAfter(dataTest));
				} else {
					result = dataTest.includes(('' + value).toUpperCase());
				}
			}
			return result;
		}));
	}

	/**
	 * no options given for a filter type of SELECT so look through data for possible values
	 *
	 * @param columnInformation ColumnInformation the column information about what field and other information
	 */
	determineFilterTypeOptions(columnInformation) {
		// _.union gives unique values
		return _.union(this.props.list
			// pull all possible values from the column
			.map(data => data[columnInformation.field])
			// remove blank values
			.filter(value => value || (value === false)))
			// convert to options object
			.map(value => {return {value: value, label: `${value}`};});
	}


	render() {
		let bottom = undefined;
		let totals = undefined;
		let spinner = undefined;
		let filters = undefined;
		let data = this.state.filterList || this.props.list;
		let limit = this.state.showLimit ? this.state.showLimit : data.length;

		// ==== AJAX Spinner ==== //
		if (this.props.ajaxSpinner) {
			spinner = (
				<AjaxSpinner backdropType={AjaxSpinner.BACKDROPS.COVER_PARENT} />
			);

		// ==== Green Button Bottom Text ==== //
		} else if (this.props.greenBottomButtonText) {
			bottom = (
				<tr key="moreRecordsRowClipped" className="more-records-row">
					<td colSpan="100">
						<a
							onClick={this.props.onGreenBottomButtonClick ? handleEvent(this.props.onGreenBottomButtonClick) : undefined}
							className={joinClassNames(
								'more-records',
								'button',
								'revdropshadowtext',
								'background-green-lighttone',
								this.props.onGreenBottomButtonClick ? '' : ' noHover')}>
							{this.props.greenBottomButtonText}
						</a>
					</td>
				</tr>
			);

		// ==== Too many results - Green Bottom Show All Button ==== //
		} else if (data.length > limit) {
			bottom = (
				<tr key="showAllRecords" className="show-all-records-row">
					<td colSpan="100"><a onClick={handleEvent(() => this.setState({showLimit: undefined}), this)} className={this.props.showLimitClassName}>Show all</a></td>
				</tr>
			)

		// ==== No Records Found ==== //
		} else if (this.props.noRecordsFound) {
			bottom = (
				<tr key="moreRecordsNoRecords" className="more-records-row"><td colSpan="100"><div id="noResults" className="bottomBlockStyle">
					There are no results. Change your search and try again.
				</div></td></tr>
			);
		}

		if (this.props.totals) {
			totals = <Row
				key='totals'
				columns={this.props.columns}
				record={this.props.totals}
			/>
		}

		if (this.props.filters) {
			filters = (
				<tr className="filters">
					{
						this.props.columns.map(c => c.filter ?
							<Filter
								key={'filter' + c.field}
								columnInformation={c}
								onChange={(field, value) => this.filterChange(field, value, c.filterType === FilterType.SELECT)}
								filterType={c.filterType}
								filterTypeOptions={c.filterTypeOptions || this.determineFilterTypeOptions(c)}
								value={this.state.filterValuesShown[c.field] === undefined ? this.defaultFilterValue(c.filterType) : this.state.filterValuesShown[c.field]}
							/> :
							<td key={'filter' + c.field}></td>)
					}
				</tr>
			);
		}

		return (
			<div className={joinClassNames(CommonAppSetup.getClassName(Components.TABLE, 'container'))}>
				<table className={joinClassNames(
					this.props.filters ? 'filters' : '',
					this.props.className,
					CommonAppSetup.getClassName(Components.TABLE, 'table')
				)}>
					<thead>
					<tr>
						{this.props.columns.map(c =>
							<HeaderRow
								key={'header' + c.field}
								title={c.title}
								tableSorterInformation={c.tableSorterInformation}
								onClick={this.headerOnRowClick}
								tableSorter={this.state.tableSorter}
								thClassName={c.thClassName}
							/>
						)}
					</tr>
					{filters}
					</thead>
					<tbody>
					{
						data.slice(0, limit)
							.map((r, i) => ({
								key: i,
								columns: this.props.columns,
								record: r,
								onRowClick: this.props.onRowClick,
								onRowDoubleClick: this.props.onRowDoubleClick,
								onRowUpdate: this.props.onRowUpdate,
								isSelected: this.props.selectedList && this.props.selectedList.includes(r),
								trClassName: this.props.trClassName,
							}))
							.map(props => this.props.onDragDrop ? <DragDropRow {...props}/> : <Row {...props}/>)
					}
					</tbody>
					<tfoot>
					{totals}
					{bottom}
					</tfoot>
				</table>
				{spinner}
			</div>
		);
	}
}

Table.defaultProps = defaultProps;
Table.constants = constants;
Table.propTypes = propTypes;

export default DragDropContext(HTML5Backend)(Table);